import { Injectable } from '@angular/core';
import { timer } from 'rxjs';
import { Observable } from 'rxjs/Observable';
import { catchError, concatMap, distinctUntilChanged, share } from 'rxjs/operators';
import { SiteWorkOrdersService } from './site-work-orders.service';
import { WorkflowItemService } from './workflow-items.service';
import { SiteIncidentsService } from './site-incidents.service';
import { NotificationService } from './notification.service';

export interface WorkOrderCountBadgeState {
  status: 'known' | 'unknown';
  count?: number;
}

export interface WorkflowItemBadgeState {
  status: 'known' | 'unknown';
  totalRecords?: number;
}

export interface EmergencyIncidentBadgeState {
  status: 'known' | 'unknown';
  incidents?: any[];
}

@Injectable()
export class NotificationBadgeService {
  workOrderCountObservable: any;
  workflowItemCountObservable: any;
  emergencyIncidentCountObservable: any;
  private hasShownWorkOrderPollingFailureToast = false;
  private hasShownWorkflowItemPollingFailureToast = false;
  private hasShownEmergencyIncidentPollingFailureToast = false;

  constructor(
    public siteWorkOrdersService: SiteWorkOrdersService,
    public workflowItemService: WorkflowItemService,
    public incidentsService: SiteIncidentsService,
    public notificationService: NotificationService,
  ) {}

  //
  // Work Orders
  //

  // A subscription to request ActionRequiredWorkOrderCount by http, but shares the request with all subscribers.
  // The http requests will stop when all subscribers unscubscribe.
  // distinctUntilChanged - subscribers are only notified if the value is different from the last value
  private getActionRequiredWorkOrderCountBase(): Observable<WorkOrderCountBadgeState> {
    if (!this.workOrderCountObservable) {
      this.workOrderCountObservable = timer(0, 20000)
        .concatMap(() => {
          return this.siteWorkOrdersService.getActionRequiredCount()
            .pipe(
              catchError((_error) => {
                if (!this.hasShownWorkOrderPollingFailureToast) {
                  this.notificationService.notify(
                    'danger',
                    'Work order count unavailable',
                    'Unable to refresh the work order count. Refresh the page if this persists.'
                  );
                  this.hasShownWorkOrderPollingFailureToast = true;
                }

                return Observable.of({
                  status: 'unknown',
                } as WorkOrderCountBadgeState);
              })
            );
        })
        .map((result: number | WorkOrderCountBadgeState) => {
          if (typeof result === 'number') {
            this.hasShownWorkOrderPollingFailureToast = false;
            return {
              status: 'known',
              count: result,
            } as WorkOrderCountBadgeState;
          }

          return result;
        })
        .pipe(
          distinctUntilChanged((previous: WorkOrderCountBadgeState, current: WorkOrderCountBadgeState) => {
            return previous.status === current.status && previous.count === current.count;
          }),
          share()
        );
    }

    return this.workOrderCountObservable;
  }

  // Use this method only once and use the error swallowing method for each subsequent subscription
  // otherwise there will be an error thrown for each subscription e.g. on status 401
  // Best added to navbar because it will always be present
  public getActionRequiredWorkOrderCountPrimary(): Observable<WorkOrderCountBadgeState> {
    return this.getActionRequiredWorkOrderCountBase();
  }

  public getActionRequiredWorkOrderCount(): Observable<WorkOrderCountBadgeState> {
    return this.getActionRequiredWorkOrderCountBase().pipe(
      catchError((_err) => {
        return Observable.of({ status: 'unknown' } as WorkOrderCountBadgeState);
      })
    );
  }

  //
  // Workflow Items
  //
  // A subscription to request the total pending workflow count for the navbar badge.
  // The http requests will stop when all subscribers unscubscribe.
  // distinctUntilChanged - subscribers are only notified if the value is different from the last value
  private getPendingWorkflowItemsCountBase(): Observable<WorkflowItemBadgeState> {
    if (!this.workflowItemCountObservable) {
      this.workflowItemCountObservable = timer(0, 20000)
        .concatMap(() => {
          return this.workflowItemService.getPendingWorkflowTotalCount()
            .pipe(
              catchError((_error) => {
                if (!this.hasShownWorkflowItemPollingFailureToast) {
                  this.notificationService.notify(
                    'danger',
                    'Workflow item count unavailable',
                    'Unable to refresh pending workflow items. Refresh the page if this persists.'
                  );
                  this.hasShownWorkflowItemPollingFailureToast = true;
                }

                return Observable.of({
                  status: 'unknown',
                } as WorkflowItemBadgeState);
              })
            );
        })
        .map((result: number | WorkflowItemBadgeState) => {
          if ((result as WorkflowItemBadgeState).status === 'unknown') {
            return result;
          }

          this.hasShownWorkflowItemPollingFailureToast = false;
          return {
            status: 'known',
            totalRecords: result as number,
          } as WorkflowItemBadgeState;
        })
        .pipe(
          distinctUntilChanged((previous: WorkflowItemBadgeState, current: WorkflowItemBadgeState) => {
            return previous.status === current.status && previous.totalRecords === current.totalRecords;
          }),
          share()
        );
    }

    return this.workflowItemCountObservable;
  }

  // Use this method only once and use the error swallowing method for each subsequent subscription
  // otherwise there will be an error thrown for each subscription e.g. on status 401
  // Best added to navbar because it will always be present
  public getPendingWorkflowItemsCountPrimary(): Observable<WorkflowItemBadgeState> {
    return this.getPendingWorkflowItemsCountBase();
  }

  public getPendingWorkflowItemsCount(): Observable<WorkflowItemBadgeState> {
    return this.getPendingWorkflowItemsCountBase().pipe(
      catchError((_err) => {
        return Observable.of({ status: 'unknown' } as WorkflowItemBadgeState);
      })
    );
  }

  //
  //Emergency Incidents
  //
  private getEmergencyIncidentCountBase(): Observable<EmergencyIncidentBadgeState> {
    if (!this.emergencyIncidentCountObservable) {
      this.emergencyIncidentCountObservable = timer(0, 20000)
        .concatMap(() => {
          return this.incidentsService.getReportedEmergencyIncident()
            .pipe(
              catchError((_error) => {
                if (!this.hasShownEmergencyIncidentPollingFailureToast) {
                  this.notificationService.notify(
                    'danger',
                    'Emergency incident status unavailable',
                    'Unable to refresh emergency incident status. Refresh the page if this persists.'
                  );
                  this.hasShownEmergencyIncidentPollingFailureToast = true;
                }

                return Observable.of({
                  status: 'unknown',
                } as EmergencyIncidentBadgeState);
              })
            );
        })
        .map((result: any[] | EmergencyIncidentBadgeState) => {
          if ((result as EmergencyIncidentBadgeState).status === 'unknown') {
            return result;
          }

          this.hasShownEmergencyIncidentPollingFailureToast = false;
          return {
            status: 'known',
            incidents: result as any[],
          } as EmergencyIncidentBadgeState;
        })
        .pipe(
          distinctUntilChanged((previous: EmergencyIncidentBadgeState, current: EmergencyIncidentBadgeState) => {
            const previousCount = previous.incidents ? previous.incidents.length : 0;
            const currentCount = current.incidents ? current.incidents.length : 0;
            return previous.status === current.status && previousCount === currentCount;
          }),
          share()
        );
    }

    return this.emergencyIncidentCountObservable;
  }

  public getEmergencyIncidentCountPrimary(): Observable<EmergencyIncidentBadgeState> {
    return this.getEmergencyIncidentCountBase();
  }

  public getEmergencyIncidentCount(): Observable<EmergencyIncidentBadgeState> {
    return this.getEmergencyIncidentCountBase().pipe(
      catchError((_err) => {
        return Observable.of({ status: 'unknown' } as EmergencyIncidentBadgeState);
      })
    );
  }
}
